import { useCallback, useEffect, useState } from "react"

import { oneOf } from "@/lib/utils"

const APPEARANCES = ["light", "dark", "system"] as const

export type Appearance = (typeof APPEARANCES)[number]

const storedAppearance = () =>
  oneOf(APPEARANCES, localStorage.getItem("appearance")) ?? "system"

const prefersDark = () => {
  if (typeof window === "undefined") {
    return false
  }
  return window.matchMedia("(prefers-color-scheme: dark)").matches
}

const applyTheme = (appearance: Appearance) => {
  const isDark =
    appearance === "dark" || (appearance === "system" && prefersDark())

  document.documentElement.classList.toggle("dark", isDark)
  document.documentElement.style.colorScheme = isDark ? "dark" : "light"
}

const mediaQuery = () => {
  if (typeof window === "undefined") {
    return null
  }

  return window.matchMedia("(prefers-color-scheme: dark)")
}

const handleSystemThemeChange = () => {
  applyTheme(storedAppearance())
}

export function initializeTheme() {
  applyTheme(storedAppearance())

  mediaQuery()?.addEventListener("change", handleSystemThemeChange)
}

/**
 * Stores the selected appearance mode, persists explicit light/dark choices,
 * and applies the effective theme class to the document root.
 */
export function useAppearance() {
  const [appearance, setAppearance] = useState<Appearance>(() => {
    if (typeof window === "undefined") return "system"
    return storedAppearance()
  })

  const updateAppearance = useCallback((mode: Appearance) => {
    setAppearance(mode)
    if (mode === "system") {
      localStorage.removeItem("appearance")
    } else {
      localStorage.setItem("appearance", mode)
    }
    applyTheme(mode)
  }, [])

  useEffect(() => {
    applyTheme(appearance)

    return () =>
      mediaQuery()?.removeEventListener("change", handleSystemThemeChange)
  }, [appearance])

  return { appearance, updateAppearance } as const
}
